// import React from "react";
// import { Link } from "react-router-dom";

// const FooterLink = ({ to, children, highlighted = false }) => (
//   <Link
//     to={to}
//     className={`transition-colors duration-300 ${highlighted ? "text-black hover:text-blue-800 " : "text-gray-600 hover:text-green-600"}`}
//   >
//     {children}
//   </Link>
// );

// export default function Footer() {
//   return (
//     <footer className="bg-white border-t border-gray-200">
//       <div className="max-w-6xl mx-auto px-4 sm:px-6">
//         <div className="py-8 md:py-12 flex flex-wrap justify-between items-center">
//           <ul className="flex flex-wrap justify-center space-x-5 mb-6 md:mb-0">
//             <li>
//               <FooterLink to="https://feedbackbyai1.lemonsqueezy.com/affiliates/">Affiliate Program</FooterLink>
//             </li>
//             <li>
//               <FooterLink to="/terms/">Terms</FooterLink>
//             </li>
//             <li>
//               <FooterLink to="/privacy/">Privacy</FooterLink>
//             </li>
//             <li>
//               <FooterLink to="mailto:business@feedbackbyai.com">Support</FooterLink>
//             </li>
//             <li>
//               <FooterLink to="/blog/">Blog</FooterLink>
//             </li>
//             <li>
//               <FooterLink to="https://twitter.com/feedbackbyai">Twitter</FooterLink>
//             </li>
//           </ul>
//           <div className="text-sm text-gray-600">© 2025 FeedbackbyAI. All rights reserved.</div>
//         </div>
//       </div>
//     </footer>
//   );
// }

import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Twitter, Mail, Shield, FileText, Heart, ExternalLink } from "lucide-react";

const FooterLink = ({ to, children, highlighted = false, external = false }) => {
  const baseClasses = "transition-colors duration-300 text-sm flex items-center hover:text-emerald-600";
  const colorClasses = highlighted ? "text-emerald-700" : "text-gray-600";

  const content = (
    <>
      {children}
      {external && <ExternalLink className="ml-1 h-3 w-3" />}
    </>
  );

  if (to.startsWith("http") || to.startsWith("mailto")) {
    return (
      <a
        href={to}
        className={`${baseClasses} ${colorClasses}`}
        target={to.startsWith("http") ? "_blank" : undefined}
        rel={to.startsWith("http") ? "noopener noreferrer" : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <Link to={to} className={`${baseClasses} ${colorClasses}`}>
      {content}
    </Link>
  );
};

export default function EnhancedFooter() {
  const mainLinks = [
    { name: "Brainstorm Idea", path: "/ideate/" },
    { name: "Business Plan", path: "/dynamicreports/" },
    { name: "Buyer Intent Search", path: "/leadfinder/" },
    { name: "TikTok & Reels", path: "/video/" },
    { name: "Free Startup Idea Validator", path: "/tools/free-startup-idea-validator/", highlighted: true },
  ];

  const resourceLinks = [
    { name: "Blog", path: "/blog/" },
    // { name: "Case Studies", path: "/case-studies/" },
    // { name: "Pricing", path: "/pricing/" },
    { name: "Affiliate Program", path: "https://feedbackbyai1.lemonsqueezy.com/affiliates/", external: true },
  ];

  const legalLinks = [
    { name: "Terms", path: "/terms/" },
    { name: "Privacy", path: "/privacy/" },
  ];

  const contactLinks = [
    { name: "Support", path: "mailto:business@feedbackbyai.com", icon: <Mail className="h-4 w-4 mr-1.5" /> },
    { name: "Twitter", path: "https://twitter.com/feedbackbyai", icon: <Twitter className="h-4 w-4 mr-1.5" />, external: true },
  ];

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Column 1: About */}
          <div>
            <div className="flex items-center mb-4">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4 text-white"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <span className="font-semibold text-gray-900">FeedbackbyAI</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              AI-powered tools to help entrepreneurs validate ideas, create business plans, find customers, and grow their business.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              Get started for free <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Column 2: Products */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Products</h3>
            <ul className="space-y-3">
              {mainLinks.map((link, index) => (
                <li key={index}>
                  <FooterLink to={link.path} highlighted={link.highlighted}>
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Resources</h3>
            <ul className="space-y-3">
              {resourceLinks.map((link, index) => (
                <li key={index}>
                  <FooterLink to={link.path} external={link.external}>
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Legal & Contact */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Support</h3>
            <ul className="space-y-3 mb-6">
              {contactLinks.map((link, index) => (
                <li key={index}>
                  <FooterLink to={link.path} external={link.external}>
                    {link.icon}
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>

            <h3 className="text-sm font-semibold text-gray-900 mb-4">Legal</h3>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <FooterLink to={link.path}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-gray-600 mb-4 md:mb-0">© {new Date().getFullYear()} FeedbackbyAI. All rights reserved.</div>
          <div className="flex items-center space-x-2 text-sm text-gray-500"></div>
        </div>
      </div>
    </footer>
  );
}
